import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { usePartnerInvitationValidate } from "@/features/partners/api";

import { AuthScreen } from "./auth-screen";

/**
 * Public landing for a partner/member invitation link (`/invitation/$token`).
 * Validates the token and either sends the invitee into signup (email prefilled,
 * token carried through to auto-accept on register) or explains why the link no
 * longer works — parity with the legacy AcceptInvitationView (A-5).
 */
export function PartnerInvitationPage() {
  const { t } = useTranslation();
  const { token } = useParams({ from: "/invitation/$token" });
  const query = usePartnerInvitationValidate(token);

  if (query.isPending) {
    return (
      <AuthScreen>
        <p className="text-sm text-muted-foreground">{t("auth.invite.loading")}</p>
      </AuthScreen>
    );
  }

  const status = query.data?.status ?? "invalid";
  const email = query.data?.email;

  if (status === "valid") {
    return (
      <AuthScreen>
        <div className="space-y-4">
          <h1 className="text-xl font-semibold">{t("auth.invite.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("auth.invite.validBody")}
          </p>
          <Button asChild>
            <Link to="/signup" search={{ token, email: email ?? undefined }}>
              {t("auth.invite.cta")}
            </Link>
          </Button>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <div className="space-y-4">
        <h1 className="text-xl font-semibold">{t("auth.invite.title")}</h1>
        <p className="text-sm text-destructive">{t(`auth.invite.${status}`)}</p>
        <Button variant="outline" asChild>
          <Link to="/login">{t("auth.invite.toLogin")}</Link>
        </Button>
      </div>
    </AuthScreen>
  );
}
